import { FilterListResponse } from '@/modules/product/api/productList';
import { FetchClient, storeClient } from '@/shared/api/client';
import { SeoListItem, SeoResponse } from '@/shared/api/types';
import { OrderBy } from '@/shared/types/order-by';

import {
  BrandProductListResponse,
  BrandRedirectResponse,
  BrandsListResponse,
  CrossRewriteBrandResponse,
} from './types';

export const fetchBrandList = async (
  fetch?: FetchClient
): Promise<BrandsListResponse> => {
  try {
    return await storeClient<BrandsListResponse>(
      '/brand/list',
      {
        method: 'GET',
      },
      fetch
    );
  } catch (e) {
    return {
      regular: [],
      premium: [],
    };
  }
};

export const fetchBrandProducts = async (
  brand: string,
  page: number = 1,
  limit: number = 50,
  ascending: number = 1,
  orderBy: OrderBy = 'sales_ratio',
  filters: Record<string, unknown> = {},
  fetch?: FetchClient
): Promise<BrandProductListResponse> => {
  try {
    const result = await storeClient<BrandProductListResponse>(
      `/brand/product-list-grid/${brand}`,
      {
        queryParams: {
          page,
          limit,
          ascending,
          orderBy,
          filters,
        },
      },
      fetch
    );

    return result;
  } catch {
    return {
      count: -1,
      data: [],
      brandId: '',
      brandName: '',
      defaultCalendar: {},
      localeCalendar: {},
      shippingDeliverySettings: {
        isCutoff: 0,
        immediateShippingDelay: 0,
        logisticDelay: 0,
      },
      breadcrumbs: [],
    };
  }
};

export const fetchBrandSeo = async (
  fetch?: FetchClient
): Promise<SeoResponse> => {
  try {
    const result = await storeClient<SeoResponse>(`/brand/seo`, {}, fetch);

    return result;
  } catch (e) {
    return {
      locale: '',
      canonical: '',
      robots: '',
      meta: {
        description: '',
        title: '',
        canonical: null,
      },
    };
  }
};

export const fetchBrandListSeo = async (
  brandSlug: string,
  fetch?: FetchClient
): Promise<SeoListItem> => {
  try {
    const result = await storeClient<SeoListItem>(
      brandSlug.includes('/')
        ? `/brand-rewrite/seo/${brandSlug}`
        : `/brand/seo/${brandSlug}`,
      {},
      fetch
    );

    return result;
  } catch {
    return {
      bottomDescription: '',
      canonical: '',
      locale: '',
      meta: {
        description: '',
        title: '',
        canonical: null,
      },
      robots: '',
      topDescription: {
        h1: '',
        popularCollections: [],
      },
      url: '',
    };
  }
};

export const fetchBrandsFilterList = async (
  brandSlug: string,
  filters: Record<string, unknown> = {},
  fetch?: FetchClient
): Promise<FilterListResponse> => {
  try {
    const result = await storeClient<FilterListResponse>(
      `/brand/filter-list/${brandSlug}`,
      {
        queryParams: {
          filters,
        },
      },
      fetch
    );

    return result;
  } catch {
    return [];
  }
};

export const fetchCrossRewriteBrands = async (
  slug: string,
  fetch?: FetchClient
): Promise<CrossRewriteBrandResponse> => {
  try {
    const result = await storeClient<CrossRewriteBrandResponse>(
      `/brand/cross-rewrite/${slug}`,
      {},
      fetch
    );

    if (!Array.isArray(result)) {
      return [];
    }

    return result;
  } catch (e) {
    return [];
  }
};

export const fetchBrandRedirect = async (
  slug: string,
  fetch?: FetchClient
): Promise<BrandRedirectResponse> => {
  try {
    return await storeClient<BrandRedirectResponse>(
      `/brand-redirect/get/${slug}`,
      {
        method: 'GET',
      },
      fetch
    );
  } catch (e) {
    return { redirectTo: null };
  }
};
